import {
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type FormEvent,
  type ReactNode,
} from 'react';
import {
  caretAfterSignificant,
  draftToMinor,
  formatAmountInput,
  minorToDraft,
  parseAmountInput,
  parsePastedAmount,
  settleDraft,
  significantBefore,
  suggestCategory,
  type ExpenseFields,
  type LocalDate,
  type NoteHistoryItem,
} from '@tahi/core';
import type { SpendingCategory } from '../../data/repos/spending';
import { Button, Chip, Sheet } from '../../ui';
import { cn } from '../../ui/cn';
import { copy } from './copy';
import { dayHeading, methodLabel } from './format';
import { INPUT, LABEL } from './styles';

const METHODS = ['cash', 'account'] as const;

export interface ExtraFormState {
  date: LocalDate;
  method: string | null;
  setMethod(method: string | null): void;
}

/** True when this device's locale writes 1.5 as "1,5", so a typed comma means the decimal point. */
function usesDecimalComma(): boolean {
  try {
    const parts = new Intl.NumberFormat(navigator.language).formatToParts(1.5);
    return parts.find((p) => p.type === 'decimal')?.value === ',';
  } catch {
    return false;
  }
}

export interface QuickAddFormProps {
  mode: 'add' | 'edit';
  /** Sheet title ("Add spending", "Edit spending"). */
  title: string;
  onClose(): void;
  today: LocalDate;
  categories: SpendingCategory[];
  noteHistory: NoteHistoryItem[];
  initial?: ExpenseFields;
  lockedFields?: ReadonlyArray<keyof ExpenseFields>;
  lockedReason?: string;
  error?: string | null;
  /**
   * Fields another module adds below the categories (Budget's fund picker, v0.2). v0.7.6: a
   * function gets the form's date and method, so "From" can offer cards and pick one.
   */
  extra?: ReactNode | ((form: ExtraFormState) => ReactNode);
  /** Methods other modules add after Cash and Account (Payments: its cards, v0.4). */
  methods?: readonly { value: string; label: string }[];
  /** v0.7.6: the cards are picked under "From" (in `extra`), so Details lists Cash and Account only. */
  cardsInFrom?: boolean;
  onSubmit(fields: ExpenseFields): void | Promise<void>;
}

/**
 * Quick add and edit, in a sheet. The amount is a real input (the phone's own number keyboard),
 * shown in DM Mono with a ₱ prefix and grouped as you type. Note and category are optional.
 * Enter (or the keyboard's Done key) logs it; the Log button stays visible above the keyboard.
 */
export function QuickAddForm({
  mode,
  title,
  onClose,
  today,
  categories,
  noteHistory,
  initial,
  lockedFields = [],
  lockedReason,
  error = null,
  extra,
  methods = [],
  cardsInFrom = false,
  onSubmit,
}: QuickAddFormProps) {
  const methodOptions = [
    ...METHODS.map((m) => ({ value: m as string, label: methodLabel(m) })),
    ...methods,
  ];
  const labelOf = (m: string) => methodOptions.find((o) => o.value === m)?.label ?? methodLabel(m);
  const formId = useId();
  const amountId = useId();
  const suggestionId = useId();
  const [amount, setAmount] = useState(initial ? minorToDraft(initial.amountMinor) : '');
  const [categoryId, setCategoryId] = useState<string | null>(initial?.categoryId ?? null);
  const [note, setNote] = useState(initial?.note ?? '');
  const [date, setDate] = useState<LocalDate>(initial?.date ?? today);
  const [method, setMethod] = useState<string | null>(initial?.method ?? null);
  const [showDetails, setShowDetails] = useState(false);
  const [busy, setBusy] = useState(false);
  const amountRef = useRef<HTMLInputElement>(null);
  const caret = useRef<number | null>(null);
  const [caretTick, setCaretTick] = useState(0);
  const decimalComma = useMemo(usesDecimalComma, []);

  const locked = (f: keyof ExpenseFields) => lockedFields.includes(f);
  const categoryLocked = locked('categoryId');
  const amountLocked = locked('amountMinor');

  const visible = useMemo(
    () => categories.filter((c) => !c.hidden || c.id === categoryId),
    [categories, categoryId],
  );

  const suggestion = useMemo(() => {
    if (categoryId !== null || categoryLocked) return null;
    const allowed = new Set(categories.filter((c) => !c.hidden).map((c) => c.id));
    const s = suggestCategory(note, noteHistory, { allowedCategoryIds: allowed });
    return s ? (categories.find((c) => c.id === s.categoryId) ?? null) : null;
  }, [note, noteHistory, categories, categoryId, categoryLocked]);

  const minor = draftToMinor(amount);

  // Grouping commas come and go as you type; keep the caret after the same digit.
  useLayoutEffect(() => {
    const el = amountRef.current;
    if (caret.current === null || !el || document.activeElement !== el) return;
    el.setSelectionRange(caret.current, caret.current);
    caret.current = null;
  }, [amount, caretTick]);

  function onAmountChange(e: ChangeEvent<HTMLInputElement>) {
    const el = e.target;
    const native = e.nativeEvent as InputEvent;
    let raw = el.value;
    const at = el.selectionStart ?? raw.length;
    // Pastes are handled in onAmountPaste; this covers a paste with no paste event into an empty field.
    if (native.inputType === 'insertFromPaste' && amount === '') {
      const next = parsePastedAmount(raw);
      caret.current = formatAmountInput(next).length;
      setAmount(next);
      setCaretTick((t) => t + 1);
      return;
    }
    // A comma typed on a decimal-comma keyboard is the decimal point; elsewhere it's grouping.
    if (native.data === ',' && decimalComma && !amount.includes('.')) {
      raw = `${raw.slice(0, at - 1)}.${raw.slice(at)}`;
    }
    const next = parseAmountInput(raw);
    caret.current = caretAfterSignificant(formatAmountInput(next), significantBefore(raw, at));
    setAmount(next);
    setCaretTick((t) => t + 1);
  }

  // A paste replaces the amount with the pasted text, read on its own: re-reading the whole field
  // would mix in its grouping commas ("1,000" + "50" read as ₱1.00).
  function onAmountPaste(e: ClipboardEvent<HTMLInputElement>) {
    if (amountLocked) return;
    e.preventDefault();
    const next = parsePastedAmount(e.clipboardData.getData('text'));
    caret.current = formatAmountInput(next).length;
    setAmount(next);
    setCaretTick((t) => t + 1);
  }

  async function submit(e?: FormEvent) {
    e?.preventDefault();
    if (minor === null || busy) return;
    setBusy(true);
    try {
      const trimmed = note.trim();
      await onSubmit({
        amountMinor: minor,
        categoryId,
        note: trimmed === '' ? null : trimmed,
        date,
        method,
      });
    } finally {
      setBusy(false);
    }
  }

  const footer = (
    <>
      {error ? (
        <p role="alert" className="mb-2 text-sm text-destructive">
          {error}
        </p>
      ) : null}
      <Button
        type="submit"
        form={formId}
        variant="primary"
        disabled={minor === null || busy}
        className="min-h-12 w-full text-lg"
      >
        {mode === 'add' ? copy.log : copy.save}
      </Button>
    </>
  );

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()} title={title} footer={footer}>
      <form
        id={formId}
        onSubmit={(e) => void submit(e)}
        onKeyDown={(e) => {
          // Enter in any field logs it (implicit submission doesn't reach a footer button everywhere).
          if (e.key !== 'Enter' || (e.target as HTMLElement).tagName !== 'INPUT') return;
          if ((e.target as HTMLInputElement).type === 'date') return;
          e.preventDefault();
          void submit();
        }}
        className="flex flex-col gap-5 pb-2"
      >
        <div className="flex flex-col gap-1">
          <label htmlFor={amountId} className={LABEL}>
            {copy.amountLabel}
          </label>
          <div
            className={cn(
              'flex items-baseline gap-2 border-b-2 pb-1 transition-colors',
              amountLocked ? 'border-divider' : 'border-border focus-within:border-accent',
            )}
          >
            <span aria-hidden className="font-mono text-[28px] text-muted">
              ₱
            </span>
            <input
              ref={amountRef}
              id={amountId}
              data-testid="amount-input"
              data-autofocus={mode === 'add' && !amountLocked ? '' : undefined}
              aria-label={copy.amountAria}
              inputMode="decimal"
              enterKeyHint="done"
              autoComplete="off"
              placeholder="0"
              value={formatAmountInput(amount)}
              readOnly={amountLocked}
              onChange={onAmountChange}
              onPaste={onAmountPaste}
              onBlur={() => setAmount((a) => settleDraft(a))}
              className="min-w-0 flex-1 bg-transparent font-mono text-[40px] leading-tight tabular-nums text-fg outline-none placeholder:text-muted/60 read-only:text-muted"
            />
          </div>
          {lockedReason ? <p className="text-sm text-muted">{lockedReason}</p> : null}
        </div>

        <input
          aria-label={copy.notePlaceholder}
          placeholder={copy.notePlaceholder}
          value={note}
          maxLength={200}
          enterKeyHint="done"
          disabled={locked('note')}
          onChange={(e) => setNote(e.target.value)}
          className={INPUT}
        />

        {!categoryLocked ? (
          <div role="group" aria-label={copy.categoryLabel} className="flex flex-col gap-2">
            <p aria-hidden className={LABEL}>
              {copy.categoryLabel}
            </p>
            {suggestion ? (
              <p id={suggestionId} className="sr-only">
                {copy.suggestedLabel(suggestion.name)}
              </p>
            ) : null}
            <div className="flex flex-wrap gap-2">
              {visible.map((c) => {
                const suggested = suggestion?.id === c.id;
                const selected = c.id === categoryId;
                return (
                  <Chip
                    key={c.id}
                    selected={selected}
                    aria-pressed={selected}
                    aria-describedby={suggested ? suggestionId : undefined}
                    onClick={() => setCategoryId((id) => (id === c.id ? null : c.id))}
                    suggested={suggested}
                  >
                    {c.name}
                    {suggested ? (
                      <span aria-hidden className="text-xs text-accent-text">
                        {copy.suggestedTag}
                      </span>
                    ) : null}
                  </Chip>
                );
              })}
            </div>
          </div>
        ) : null}

        {typeof extra === 'function' ? extra({ date, method, setMethod }) : extra}

        <div className="flex flex-col gap-3">
          <button
            type="button"
            aria-expanded={showDetails}
            onClick={() => setShowDetails((s) => !s)}
            className="min-h-11 self-start rounded-full text-sm text-primary hover:underline"
          >
            {dayHeading(date, today)}
            {method ? ` · ${labelOf(method)}` : ''} ·{' '}
            {showDetails ? copy.hideDetails : copy.details}
          </button>

          {showDetails ? (
            <div className="flex flex-col gap-3">
              <label className="flex flex-col gap-1 text-sm text-muted">
                {copy.dateLabel}
                <input
                  type="date"
                  value={date}
                  max={today}
                  disabled={locked('date')}
                  onChange={(e) => e.target.value && setDate(e.target.value)}
                  className={INPUT}
                />
              </label>
              <div role="group" aria-label={copy.methodLabel} className="flex flex-wrap gap-2">
                {methodOptions
                  .filter((o) => !(cardsInFrom && o.value.startsWith('card:')))
                  .map(({ value: m, label }) => (
                    <Chip
                      key={m}
                      disabled={locked('method')}
                      selected={method === m}
                      aria-pressed={method === m}
                      onClick={() => setMethod((v) => (v === m ? null : m))}
                    >
                      {label}
                    </Chip>
                  ))}
              </div>
            </div>
          ) : null}
        </div>
      </form>
    </Sheet>
  );
}
